import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    DATE_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
} from "../../../helpers/constants";
import { useAgreement, useAgreementPayments } from "../../../services/agreementsService";
import { useInvestments } from "../../../services/productsService";
import { SendPaymentNotificationButton } from "../../AgreementsPage/SendPaymentNotificationButton";

export const MASS_NOTIFICATIONS_TABLE_NAME = "mass_notifications_table";

export const useMassNotificationsTableConfig = () => {
    const { t } = useTranslation();

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    return useMemo(
        () => [
            {
                name: "investment",
                getValueCallback: (rowData) => {
                    return (
                        <div style={{ minHeight: "45px", display: "flex", alignItems: "center" }}>
                            <InvestmentCell agreementId={rowData.agrepay_agreement} />
                        </div>
                    );
                },
                label: t("massNotifications.investmentName"),
                filterType: SELECT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    optionKey: "value",
                    optionLabel: "label",
                    options: investments,
                    multiple: false,
                }),
                fieldConfig: {
                    optionKey: "value",
                    optionLabel: "label",
                    options: investments,
                },
            },
            {
                name: "agreementNumber",
                getValueCallback: (rowData) => {
                    return <AgreementNumberCell agreementId={rowData.agrepay_agreement} />;
                },
                label: t("massNotifications.agreementNumber"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
            },
            {
                name: "customerNames",
                getValueCallback: (rowData) => {
                    return <CustomerNamesCell agreementId={rowData.agrepay_agreement} />;
                },
                label: t("massNotifications.customerNames"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
            },
            {
                name: "paymentIndex",
                getValueCallback: (rowData) => {
                    return (
                        <PaymentIndexCell
                            paymentId={rowData.id}
                            agreementId={rowData.agrepay_agreement}
                        />
                    );
                },
                label: t("massNotifications.paymentIndex"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
            },
            {
                name: "dueDate",
                getValueCallback: (rowData) => rowData.agrepay_due_date || "-",
                label: t("massNotifications.dueDate"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
            },
            {
                name: "sendDate",
                getValueCallback: (rowData) => rowData.agrepay_notification_send_date || "-",
                label: t("massNotifications.sendDate"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
            },
            {
                name: "sendNotification",
                getValueCallback: (rowData) => {
                    return <SendNotificationCell payment={rowData} />;
                },
                label: t("massNotifications.sendNotification"),
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
            },
        ],
        [t],
    );
};

const InvestmentCell = ({ agreementId }) => {
    const { data: agreement } = useAgreement(agreementId);

    if (!agreement?.products?.[0]?.product?.investment) {
        return <>-</>;
    }

    return <>{agreement.products[0].product.investment.investment_name_sales}</>;
};

const AgreementNumberCell = ({ agreementId }) => {
    const { data: agreement } = useAgreement(agreementId);

    return <>{agreement?.agreement_nr || "-"}</>;
};

const CustomerNamesCell = ({ agreementId }) => {
    const { data: agreement } = useAgreement(agreementId);

    if (!agreement?.shares) {
        return <>-</>;
    }

    const customerNames = agreement.shares
        .flatMap((share) => share.customers)
        .map((customer) =>
            `${customer.person?.name || ""} ${customer.person?.surname || ""}`.trim(),
        )
        .filter((name) => name.length > 0)
        .join("; ");

    return <>{customerNames || "-"}</>;
};

const PaymentIndexCell = ({ paymentId, agreementId }) => {
    const params = new URLSearchParams();
    params.set("agreement", agreementId);

    const { data: paymentsResponse } = useAgreementPayments(params);

    if (!paymentsResponse?.results) {
        return <>-</>;
    }

    const index = paymentsResponse.results.findIndex((p) => p.id === paymentId);

    return <>{index !== -1 ? index + 1 : "-"}</>;
};

const SendNotificationCell = ({ payment }) => {
    const { data: agreement } = useAgreement(payment.agrepay_agreement);
    const params = new URLSearchParams();
    params.set("agreement", payment.agrepay_agreement);
    const { data: paymentsResponse } = useAgreementPayments(params);

    if (!agreement || !paymentsResponse?.results) {
        return <>-</>;
    }

    const paymentIndex = paymentsResponse.results.findIndex((p) => p.id === payment.id);

    return (
        <SendPaymentNotificationButton
            agreement={agreement}
            payment={payment}
            paymentIdx={paymentIndex + 1}
            allPayments={paymentsResponse.results}
        />
    );
};
